<template>
  <main class="tpm-monitor-page">
    <section class="kpi-strip">
      <div class="kpi-card">
        <div class="kpi-label">启用 TPM 模型</div>
        <div class="kpi-value">{{ stats.total }}</div>
      </div>
      <div class="kpi-card ok">
        <div class="kpi-label">正常</div>
        <div class="kpi-value">{{ stats.ok }}</div>
      </div>
      <div class="kpi-card notice">
        <div class="kpi-label">低水位</div>
        <div class="kpi-value">{{ stats.low }}</div>
      </div>
      <div class="kpi-card alarm">
        <div class="kpi-label">告警 <span class="kpi-sub">已超卖 / 限流冷却</span></div>
        <div class="kpi-value">{{ stats.alert }}</div>
      </div>
    </section>

    <section class="soft-card table-panel">
      <div class="toolbar">
        <span class="page-title">压力排行 Top 10</span>
        <span class="hint">按剩余预算比例排序，越靠前越紧张</span>
        <el-button size="small" plain icon="el-icon-refresh" @click="loadTpmData">刷新</el-button>
        <span class="last-check" v-if="tpmCheckedAt">上次检查：{{ tpmCheckedAt }}</span>
      </div>
      <div ref="pressureChart" class="chart-container"></div>
      <div v-if="tpmLoading" class="loading-tip">加载中...</div>
      <div v-if="tpmError" class="error-tip">
        <el-alert :title="tpmError" type="error" show-icon :closable="false" />
      </div>
    </section>

    <section class="soft-card table-panel">
      <div class="toolbar">
        <span class="page-title">TPM 降级记录</span>
        <el-button size="small" plain icon="el-icon-refresh" @click="loadTpmDegradations">刷新</el-button>
        <span class="last-check" v-if="degCheckedAt">上次检查：{{ degCheckedAt }}</span>
      </div>

      <el-table v-if="degradations.length" :data="degradations" size="small" style="width: 100%">
        <el-table-column label="时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="源模型" min-width="180">
          <template slot-scope="scope">
            <div class="model-name">{{ scope.row.sourceModel }}</div>
          </template>
        </el-table-column>
        <el-table-column label="降级目标" min-width="150">
          <template slot-scope="scope">
            <div class="model-name">{{ scope.row.targetModel }}</div>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="empty">暂无 TPM 降级记录</div>
      <el-pagination
        v-if="degTotal > 0"
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="degTotal"
        :page-size="degSize"
        :current-page.sync="degPage"
        @current-change="onDegPageChange"
      />
      <div v-if="degLoading" class="loading-tip">加载中...</div>
      <div v-if="degError" class="error-tip">
        <el-alert :title="degError" type="error" show-icon :closable="false" />
      </div>
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'TpmMonitor',
  data() {
    return {
      raw: [],
      tpmLoading: false,
      tpmError: '',
      tpmCheckedAt: '',
      autoTimer: null,
      degradations: [],
      degLoading: false,
      degError: '',
      degCheckedAt: '',
      degPage: 1,
      degSize: 20,
      degTotal: 0,
      chart: null,
      statusBarColor: {
        ok: '#34c98f',
        low: '#f4b74f',
        oversold: '#ef6d7a',
        cooling: '#ef6d7a'
      }
    }
  },
  computed: {
    decorated() {
      return this.raw.map(row => this.decorateRow(row))
    },
    stats() {
      const s = { total: this.decorated.length, ok: 0, low: 0, alert: 0 }
      for (const row of this.decorated) {
        if (row.statusKey === 'ok') s.ok++
        else if (row.statusKey === 'low') s.low++
        else s.alert++
      }
      return s
    },
    pressureItems() {
      return this.decorated
        .filter(row => row.capacity > 0)
        .map(row => ({
          modelName: row.modelName,
          supplierName: row.supplierName,
          usedPct: row.usedPct,
          remainingPct: row.tokens / row.capacity,
          tokens: row.tokens,
          capacity: row.capacity,
          statusKey: row.statusKey
        }))
        .sort((a, b) => a.remainingPct - b.remainingPct)
        .slice(0, 10)
    }
  },
  mounted() {
    this.loadTpmData()
    this.loadTpmDegradations()
    this.startAutoRefresh()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.autoTimer = setInterval(() => {
        this.loadTpmData(true)
      }, 30000)
    },
    stopAutoRefresh() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer)
        this.autoTimer = null
      }
    },
    onResize() {
      if (this.chart) this.chart.resize()
    },
    async loadTpmData(silent) {
      if (!silent) this.tpmLoading = true
      try {
        const res = await this.$http.get('/admin/monitoring/tpm-buckets')
        this.raw = res || []
        this.tpmError = ''
        this.tpmCheckedAt = new Date().toLocaleTimeString('zh-CN', { hour12: false })
        this.$nextTick(() => this.renderChart())
      } catch (e) {
        this.tpmError = (e && e.response && e.response.data && e.response.data.error) || '加载失败'
      } finally {
        this.tpmLoading = false
      }
    },
    onDegPageChange(page) {
      this.degPage = page
      this.loadTpmDegradations()
    },
    async loadTpmDegradations() {
      this.degLoading = true
      try {
        const res = await this.$http.get('/admin/monitoring/tpm-degradations', {
          params: {
            page: this.degPage,
            pageSize: this.degSize
          }
        })
        this.degradations = res.records || []
        this.degTotal = Number(res.total) || 0
        this.degError = ''
        this.degCheckedAt = new Date().toLocaleTimeString('zh-CN', { hour12: false })
      } catch (e) {
        this.degError = (e && e.response && e.response.data && e.response.data.error) || '加载失败'
      } finally {
        this.degLoading = false
      }
    },
    decorateRow(row) {
      const tpmLimit = row.tpm_limit != null ? Number(row.tpm_limit) : 0
      const capacity = Math.max(1, tpmLimit)
      const tokens = row.tokens != null ? Number(row.tokens) : 0
      const draining = (row.drain_until_ms || 0) > Date.now()
      let statusKey = 'ok'
      if (tokens < 0) {
        statusKey = 'oversold'
      } else if (draining) {
        statusKey = 'cooling'
      } else if (tokens < capacity * 0.15) {
        statusKey = 'low'
      }
      const usedPct = capacity > 0 ? Math.min(100, Math.max(0, (1 - tokens / capacity) * 100)) : 0
      return {
        supplierName: row.supplier_name || '-',
        modelName: row.model_name || ('ID ' + row.id),
        capacity,
        tokens,
        usedPct,
        statusKey
      }
    },
    renderChart() {
      const el = this.$refs.pressureChart
      if (!el) return
      if (this.chart) this.chart.dispose()
      this.chart = echarts.init(el)
      const items = this.pressureItems
      if (!items.length) {
        this.chart.clear()
        return
      }
      const short = s => (s.length > 36 ? s.slice(0, 35) + '…' : s)
      const axisLabel = i => {
        const showSupplier = i.supplierName && i.supplierName !== '-' && i.supplierName !== i.modelName
        return short(showSupplier ? `${i.modelName} · ${i.supplierName}` : i.modelName)
      }
      this.chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const p = params[0]
            const item = items[p.dataIndex]
            return [
              `<b>${item.modelName}</b>`,
              `<div style="font-size:12px;color:#8a9199;">${item.supplierName}</div>`,
              `<div>配置 TPM：<b>${this.formatNum(item.capacity)}</b></div>`,
              `<div>当前剩余：<b>${this.formatNum(item.tokens)}</b></div>`,
              `<div>使用率：<b style="color:${this.statusTextColor(item.statusKey)};">${item.usedPct.toFixed(1)}%</b></div>`
            ].join('')
          }
        },
        grid: { left: 270, right: 60, top: 8, bottom: 8 },
        xAxis: {
          type: 'value',
          max: 100,
          axisLabel: { formatter: '{value}%', color: '#9aa4af' },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.08)' } }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: items.map(i => axisLabel(i)),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#dbe8fb', fontSize: 12 }
        },
        series: [
          {
            type: 'bar',
            data: items.map(i => ({
              value: i.usedPct,
              itemStyle: {
                color: this.statusBarColor[i.statusKey],
                borderRadius: [0, 4, 4, 0]
              }
            })),
            barWidth: 14,
            label: {
              show: true,
              position: 'right',
              formatter: p => this.formatNum(items[p.dataIndex].usedPct) + '%',
              color: '#dbe8fb',
              fontSize: 12,
              fontWeight: 700
            }
          }
        ]
      })
    },
    statusTextColor(key) {
      return {
        ok: '#34c98f',
        low: '#f4b74f',
        oversold: '#ef6d7a',
        cooling: '#ef6d7a'
      }[key]
    },
    formatTime(t) {
      if (!t) return '-'
      return String(t).replace('T', ' ').slice(0, 19)
    },
    formatNum(n) {
      if (n == null || isNaN(Number(n))) return '0'
      const v = Number(n)
      if (v >= 100000000) return (v / 100000000).toFixed(1) + '亿'
      if (v >= 10000) return (v / 10000).toFixed(1) + '万'
      return v.toLocaleString('en-US')
    }
  }
}
</script>

<style scoped>
.tpm-monitor-page {
  padding: 18px 24px 76px;
}
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.kpi-card {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 18px 22px;
  border-left: 4px solid var(--blue);
}
.kpi-card.ok { border-left-color: var(--green); }
.kpi-card.notice { border-left-color: var(--amber); }
.kpi-card.alarm { border-left-color: var(--red); }
.kpi-label {
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 8px;
}
.kpi-sub {
  font-size: 12px;
  color: var(--muted-2);
}
.kpi-value {
  font-size: 30px;
  font-weight: 700;
  color: var(--text);
  line-height: 1;
}
.kpi-card.ok .kpi-value { color: var(--green); }
.kpi-card.notice .kpi-value { color: var(--amber); }
.kpi-card.alarm .kpi-value { color: var(--red); }
.table-panel {
  margin-top: 20px;
  padding: 18px;
}
.chart-container {
  height: 320px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.page-title {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  color: var(--text);
}
.hint {
  font-size: 12px;
  color: var(--muted-2);
}
.last-check {
  font-size: 12px;
  color: var(--muted-2);
}
.model-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}
.empty {
  color: var(--muted-2);
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}
.loading-tip {
  text-align: center;
  padding: 40px;
  color: var(--muted-2);
  font-size: 13px;
}
.error-tip {
  margin-top: 16px;
}
.pagination-bar {
  margin-top: 16px;
  text-align: right;
}
</style>